import * as icons from '@app/page/src/assets/icons'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Button } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { FC, KeyboardEvent } from 'react'
import { FIRST_MONTH_FREE } from '@app/common/src/constants/promoCodes'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { Input } from '@dtx-company/shared-components/src/components/atoms/Input/index'
import { PPEPromoCodeState } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePPEPromoCodeState'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { fireHeapEvent } from '@dtx-company/true-common/src/utils/heap'
import { useDebouncedCallback } from 'use-debounce'

export type PromoCodeInputProps = {
  promoCode: string
  setPromoCode: (promoCode: string) => void
  onApply: (promoCode: string) => Promise<void>
} & Pick<PPEPromoCodeState, 'state'>

const PromoCodeInput: FC<PromoCodeInputProps> = ({ promoCode, setPromoCode, state, onApply }) => {
  /** Validate promoCode text when the user presses the Enter key in the field */
  function onInputKeyPress(event: KeyboardEvent<HTMLInputElement>): void {
    const target = event.target as HTMLInputElement // For some reason TS can't deduce event.target is for HTMLInputElement
    if (event.key === 'Enter') onApply(target.value)
  }

  const trackPromoCodeChanges = useDebouncedCallback((promoText: string) => {
    fireHeapEvent('Revenue_Upgrade_Changed_PromoCode', { promoText })
  }, 300)

  const _PromoHelperText = (): JSX.Element | null => {
    if (state === 'invalid')
      return (
        <Text variant="body/small" mt="4px" color="red">
          Invalid promo code
        </Text>
      )

    if (state === 'valid') {
      if (promoCode === FIRST_MONTH_FREE) {
        return <FirstMonthFreeCouponApplied />
      }

      return (
        <Text variant="body/small" mt="4px" color="green">
          Discount applied
        </Text>
      )
    }
    return null
  }

  const applyBtnDisabled = state !== 'initial'
  return (
    <Box position="relative" flexDirection="column">
      <Text variant="body/small" mb="10px">
        Enter promotion code
      </Text>
      <Input
        value={promoCode}
        maxWidth="none"
        width="100%"
        placeholder="Promotioncode10"
        type="text"
        data-testid={'promo-code-input'}
        error={state === 'invalid'}
        startAdornment={<Icon height={20} width={40} alt="" src={icons.coupon} />}
        endAdornment={
          <Button
            type="button"
            disabled={applyBtnDisabled}
            height="32px"
            width="62px"
            colorVariant="primary"
            marginRight="5px"
            onClick={() => {
              // We don't need an onClick because clicking this button would blur
              // the field which would call onApply
            }}
          >
            <Text color="inherit" fontSize="14px" fontWeight="bold">
              {state === 'valid' ? 'Applied' : 'Apply'}
            </Text>
          </Button>
        }
        onKeyPress={onInputKeyPress}
        disabled={state === 'validating'}
        onChange={e => {
          trackPromoCodeChanges(e.target.value)
          setPromoCode(e.target.value)
        }}
        onFocus={_event => {
          fireHeapEvent('Revenue_Upgrade_Focused_PromoCodeTextField')
        }}
        onBlur={event => {
          onApply(event.target.value)
        }}
      />
      <_PromoHelperText />
    </Box>
  )
}

export default PromoCodeInput

const FirstMonthFreeCouponApplied = (): JSX.Element => (
  <Box
    backgroundImage="url('/images/green-tag.png')"
    backgroundSize="contain"
    backgroundRepeat="no-repeat"
    py="8px"
    px="24px"
    mt="8px"
  >
    <Text variant="body/large" color={'white'}>
      {`${FIRST_MONTH_FREE} applied`}
    </Text>
  </Box>
)
